import { DataTable, dateTruncFromGranularity, EffectiveDefinition, ValueAccessor } from '../../../models';
import { DefaultTimestampAccessor } from '../../../models/Accessor';
import { applyWindowFunction, DateWindowFunction, DateWindowTypes } from '../core';
import { DefinedStartDateWindow, definedStartDateWindow } from '../types/definedStartDateWindow';
import { LatestDataDateWindow, latestDataDateWindow } from '../types/latestDataDateWindow';
import { CustomDateWindow, customDateWindow } from '../types/customDateWindow';
import { LastNDateWindow, lastNDateWindow } from '../types/lastNDateWindow';
import { allTimeDateWindow, ALL_TIME_DATE_WINDOW, SpecialDateWindow } from '../types/allTimeDateWindow';
import { getTimestampFeature } from '../../metricValue';
import { columnSchemaForRef, isDateTimeColumnSchema } from '../../../models/EffectiveDefinition';
import { defaultDateWindow } from './defaultDateWindow';
import { DateWindow } from './DateWindow';
import { areDurationsEqual, areRangesEqual } from '../../../dateUtils';

export { DateWindow } from './DateWindow';

export { dateWindowOptions } from './dateWindowOptions';
export { defaultDateWindow } from './defaultDateWindow';
export {
  displayDateWindow,
  displayStringFromWindow,
  granularityFromWindow,
  getLabelForWindowPickerOption,
} from './displayDateWindow';

export const getFirstWindowOrDefault = (definition: EffectiveDefinition): DateWindow => {
  return getFirstWindow(definition) ?? defaultDateWindowFor(definition);
};

export const getFirstWindow = (definition: EffectiveDefinition): DateWindow | undefined => {
  return definition.features.dateWindow?.dateWindows?.[0];
};

export const getDefinitionWithServerOrDefaultWindow = (
  definition: EffectiveDefinition,
  defaultWindow: DateWindow,
  dateWindowEnabled: boolean
): EffectiveDefinition => {
  const window = (definition && getFirstWindow(definition)) ?? defaultWindow;
  return definition && definitionWithWindow(definition, dateWindowEnabled ? window : defaultWindow);
};

const defaultDateWindowFor = (definition: EffectiveDefinition): DateWindow => {
  let timestampFeature = getTimestampFeature(definition.features);
  let timeColumnSchema = columnSchemaForRef(definition, timestampFeature);
  if (isDateTimeColumnSchema(timeColumnSchema)) {
    return defaultDateWindow(dateTruncFromGranularity(timeColumnSchema.dateGranularity));
  } else {
    return ALL_TIME_DATE_WINDOW;
  }
};

export const applyWindow = (
  metricTable: DataTable,
  dateWindow: DateWindow,
  accessor: ValueAccessor<Date> = DefaultTimestampAccessor
): DataTable => {
  return applyWindowFunction(metricTable, windowFuncFromWindow(dateWindow, accessor));
};

export const windowFuncFromWindow = (
  dateWindow: DateWindow,
  accessor: ValueAccessor<Date> = DefaultTimestampAccessor
): DateWindowFunction => {
  switch (dateWindow.type) {
    case DateWindowTypes.CUSTOM:
      const customWindow = dateWindow as CustomDateWindow;
      return customDateWindow(customWindow.range, accessor);
    case DateWindowTypes.LAST_N:
      const lastNWindow = dateWindow as LastNDateWindow;
      return lastNDateWindow(lastNWindow.n);
    case DateWindowTypes.LATEST_DATA:
      const latestWindow = dateWindow as LatestDataDateWindow;
      return latestDataDateWindow(latestWindow.duration, accessor);
    case DateWindowTypes.DEFINED_START:
      const definedStartDateDateWindow = dateWindow as DefinedStartDateWindow;
      return definedStartDateWindow(definedStartDateDateWindow.startDate, accessor);
    case DateWindowTypes.SPECIAL:
    default:
      return allTimeDateWindow();
  }
};

export const definitionWithWindow = (definition: EffectiveDefinition, dateWindow: DateWindow): EffectiveDefinition => {
  const newFeatures = { ...definition.features, dateWindow: { dateWindows: [dateWindow] } };
  return { ...definition, features: newFeatures };
};

export const areWindowsEqual = (window: DateWindow, other: DateWindow): boolean => {
  if (window.type !== other.type) {
    return false;
  }

  if (isCustomWindow(window) && isCustomWindow(other)) {
    return areRangesEqual(window.range, other.range);
  } else if (isLatestDataWindow(window) && isLatestDataWindow(other)) {
    return areDurationsEqual(window.duration, other.duration);
  } else if (isLastNWindow(window) && isLastNWindow(other)) {
    return window.n === other.n;
  } else if (isSpecialWindow(window) && isSpecialWindow(other)) {
    return window.specialType === other.specialType;
  } else if (isDefinedStartWindow(window) && isDefinedStartWindow(other)) {
    return window.startDate.getTime() === other.startDate.getTime();
  }

  return false;
};

export function isLastNWindow(window: DateWindow): window is LastNDateWindow {
  return window.type === DateWindowTypes.LAST_N;
}

export function isSpecialWindow(window: DateWindow): window is SpecialDateWindow {
  return window.type === DateWindowTypes.SPECIAL;
}

export function isLatestDataWindow(window: DateWindow): window is LatestDataDateWindow {
  return window.type === DateWindowTypes.LATEST_DATA;
}

export function isCustomWindow(window: DateWindow): window is CustomDateWindow {
  return window.type === DateWindowTypes.CUSTOM;
}

export function isDefinedStartWindow(window: DateWindow): window is DefinedStartDateWindow {
  return window.type === DateWindowTypes.DEFINED_START;
}
